import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../hooks/useCart";
import type { CustomerInfo } from "../types";
import {
  PageTitle,
  FormContainer,
  FormGroup,
  Label,
  Input,
  Button,
  CartSummary,
  CartTotal,
  EmptyCart,
} from "../components/styled";

export const CheckoutPage: React.FC = () => {
  const { state, clearCart } = useCart();
  const navigate = useNavigate();

  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    firstName: "",
    lastName: "",
    email: "",
    address: "",
    city: "",
    postalCode: "",
    country: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);

  // Checked before the empty-cart branch below: placing an order clears the
  // cart, so the two conditions are both true at the end and this one wins.
  // The window must visibly read "done" - on a tiled demo screen that is the
  // signal the audience connects to the next person being admitted.
  if (orderPlaced) {
    return (
      <EmptyCart data-order-complete="true">
        <h2>Order complete</h2>
        <p>Thank you for your purchase.</p>
        <Button
          onClick={() => navigate("/")}
          style={{ width: "auto", margin: "2rem auto 0" }}
        >
          Continue Shopping
        </Button>
      </EmptyCart>
    );
  }

  if (state.items.length === 0) {
    return (
      <EmptyCart>
        <h2>No items to checkout</h2>
        <p>Your cart is empty. Add some products first!</p>
        <Button
          onClick={() => navigate("/")}
          style={{ width: "auto", margin: "2rem auto 0" }}
        >
          Continue Shopping
        </Button>
      </EmptyCart>
    );
  }

  const handleInputChange =
    (field: keyof CustomerInfo) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setCustomerInfo((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // In a real app, you would send this data to your backend
    const order = {
      id: Date.now().toString(),
      items: state.items,
      total: state.total,
      customerInfo,
      timestamp: new Date(),
    };

    console.log("Order placed:", order);

    // Hand the waiting-room slot back so the edge can admit the next person.
    // Unconditional and best-effort: with no waiting room in front of us, or no
    // queue cookie, the lobby answers 204 and nothing happens - so this same
    // code path serves both the unprotected and the gated demo runs.
    //
    // content-type MUST be application/json: the edge copies the inbound
    // Content-Type onto the lobby's outbound Gcore API call, overriding what the
    // lobby sets, and a wrong type produces a malformed API call that fails
    // looking like an auth error.
    try {
      await fetch("/_wr/sale-complete", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: "{}",
      });
    } catch {
      // never block the order on this
    }

    clearCart();
    setOrderPlaced(true);
    setIsSubmitting(false);
  };

  const isFormValid = Object.values(customerInfo).every(
    (value) => value.trim() !== ""
  );

  return (
    <>
      <PageTitle>Checkout</PageTitle>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 400px",
          gap: "3rem",
          marginTop: "2rem",
        }}
      >
        <FormContainer>
          <h2 style={{ marginTop: 0 }}>Shipping Information</h2>
          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              <FormGroup>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={customerInfo.firstName}
                  onChange={handleInputChange("firstName")}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={customerInfo.lastName}
                  onChange={handleInputChange("lastName")}
                  required
                />
              </FormGroup>
            </div>

            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={customerInfo.email}
                onChange={handleInputChange("email")}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                type="text"
                value={customerInfo.address}
                onChange={handleInputChange("address")}
                required
              />
            </FormGroup>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "1rem",
              }}
            >
              <FormGroup>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  type="text"
                  value={customerInfo.city}
                  onChange={handleInputChange("city")}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  type="text"
                  value={customerInfo.postalCode}
                  onChange={handleInputChange("postalCode")}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  type="text"
                  value={customerInfo.country}
                  onChange={handleInputChange("country")}
                  required
                />
              </FormGroup>
            </div>

            <Button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              style={{ marginTop: "1rem" }}
            >
              {isSubmitting
                ? "Processing..."
                : `Place Order - $${state.total.toFixed(2)}`}
            </Button>
          </form>
        </FormContainer>

        <div>
          <CartSummary>
            <h3 style={{ marginTop: 0 }}>Order Summary</h3>
            {state.items.map((item) => (
              <div
                key={item.product.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "1rem",
                  paddingBottom: "1rem",
                  borderBottom: "1px solid #eee",
                }}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{item.product.name}</div>
                  <div style={{ color: "#666", fontSize: "0.9rem" }}>
                    Qty: {item.quantity}
                  </div>
                </div>
                <div style={{ fontWeight: 500 }}>
                  ${(item.product.price * item.quantity).toFixed(2)}
                </div>
              </div>
            ))}
            <CartTotal>Total: ${state.total.toFixed(2)}</CartTotal>
          </CartSummary>
        </div>
      </div>
    </>
  );
};
